import { Button, Card, CardBody, Stack, Text } from '@olytic/foundation';
import { OptionCard } from '@olytic/foundation/cpq';
import { Link } from 'react-router-dom';

import { formatCurrency, humanize } from '../objects/format';
import { recordPath } from '../objects/routes';
import type { QuoteBuilder } from './quotesApi';

const money = (n: number) => formatCurrency(n);

/**
 * Options: the quotes on this quote's opportunity, side by side, as the
 * proposal builder laid them out. The recommended one is the opportunity's
 * primary quote and leads the proposal. A new option starts as a copy of this one.
 */
export function OptionsTab({
  data,
  busy,
  onDuplicate,
  onMakePrimary,
}: {
  data: QuoteBuilder;
  busy: boolean;
  onDuplicate: () => void;
  onMakePrimary: (id: string) => void;
}): JSX.Element {
  const { quote, options } = data;
  const intro = (
    <Text tone="muted">
      Lay out two or more ways to buy, side by side. Each option is its own quote on the opportunity, with its own lines and plan.
      The proposal&apos;s Options section shows the same comparison.
    </Text>
  );
  // One option is no comparison yet: the prototype's empty state.
  if (!quote.opportunity_id || options.length < 2) {
    return (
      <Stack>
        {intro}
        <Card className="border-dashed">
          <CardBody>
            <div className="flex flex-col items-center gap-2 py-6 text-center">
              <Text className="font-emphasis">No options for this quote yet</Text>
              <Text tone="muted">
                {quote.opportunity_id
                  ? 'Set up two options to compare, such as keeping the current plan with an add-on versus upgrading.'
                  : "Options are the quotes on one opportunity. Set this quote's opportunity first."}
              </Text>
              {quote.opportunity_id ? (
                <Button variant="brand" disabled={busy} onClick={onDuplicate}>
                  Set up options
                </Button>
              ) : null}
            </div>
          </CardBody>
        </Card>
      </Stack>
    );
  }
  return (
    <Stack>
      <div className="flex flex-wrap items-center justify-between gap-4">
        {intro}
        <Button variant="neutral" disabled={busy} onClick={onDuplicate}>
          Add an option
        </Button>
      </div>
      <div className="grid gap-3.5 md:grid-cols-2 lg:grid-cols-3">
        {options.map((option, i) => {
          const oneTime = option.one_time_net ?? 0;
          const monthly = option.monthly_net ?? 0;
          const discount = Math.max(0, (option.one_time_list ?? 0) - oneTime + ((option.monthly_list ?? 0) - monthly) * 12);
          const current = option.id === quote.id;
          return (
            <OptionCard
              key={option.id}
              kicker={`Option ${i + 1} · ${humanize(option.status)}`}
              name={option.name ?? option.quote_number}
              recommended={option.is_primary}
              price={money(oneTime + monthly * 12)}
              per="grand total"
              then="One-time services + 12 months of software"
              rows={[
                { key: 'one', label: 'One-Time Services', value: money(oneTime) },
                { key: 'mon', label: 'Software', value: `${money(monthly)} / mo` },
                { key: 'disc', label: 'Total Discount', value: money(discount), muted: discount === 0 },
              ]}
              footer={
                <>
                  {current ? (
                    <Text tone="muted">This quote</Text>
                  ) : (
                    <Button asChild variant="neutral" size="sm">
                      <Link to={recordPath('quotes', option.id)}>Open</Link>
                    </Button>
                  )}
                  {option.is_primary ? null : (
                    <Button variant="subtle" size="sm" disabled={busy} onClick={() => onMakePrimary(option.id)}>
                      Make recommended
                    </Button>
                  )}
                </>
              }
            />
          );
        })}
      </div>
    </Stack>
  );
}
